/*
 * SP の商品写真の横の送り。
 *
 * 上のギャラリー（media-gallery.js）と、ページ下の購入エリア再掲
 * （muori-purchase-recap.js）の両方が使う。
 *
 * - 払うと、払った向きの次の1枚へ必ず1枚だけ進む。勢いや指を離した位置で
 *   止まる写真が変わらないよう、横の動きはブラウザのスクロール（scroll-snap）
 *   に任せず、ここで受け取る。払っている間は写真が指についてくる。
 *   縦の動きはブラウザに任せ、ページのスクロールになる（CSS の
 *   `touch-action: pan-y`）。
 * - 最後の次は最初につながる。前後に1枚ずつ写し（最後の写しを先頭に、最初の
 *   写しを末尾に）を置き、写しで止まったら本物へ一瞬で移す。見た目は
 *   変わらないので、つながって見える。矢印も同じ道を通る。
 *
 * 写しは読み上げにもキーボードにも出ない（aria-hidden と inert）。id と
 * data-media-id は外す。上のギャラリーはこの2つで写真を探すので、写しを
 * 拾わないように。
 *
 * 上と下のセクションがそれぞれ読み込むので、2回目は何もしない。
 */
window.MuoriLoopSlider ??= class MuoriLoopSlider {
  constructor(list, { onSettle } = {}) {
    this.list = list;
    this.onSettle = onSettle;
    this.target = null;
    this.drag = null;
    this.animation = null;
    this.refresh();
    this.list.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
    this.list.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
    // 横に払っている間は、ページが縦にも動かないよう止める（passive にしない）。
    this.list.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
    this.list.addEventListener('touchend', () => this.onTouchEnd(false), { passive: true });
    this.list.addEventListener('touchcancel', () => this.onTouchEnd(true), { passive: true });
    // 払ったあとに写真の拡大（クリック）が開かないように。
    this.list.addEventListener(
      'click',
      (event) => {
        if (!this.dragged) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  /* 写しを除いた、本物の写真。 */
  get items() {
    return Array.from(this.list.children).filter((element) => !element.hasAttribute('data-muori-clone'));
  }

  /*
   * 写真1枚の幅。`clientWidth` は整数に丸められていて、画面幅が端数の
   * Android 端末では、枚数が増えるほど位置の計算がずれる。
   */
  get width() {
    return this.list.firstElementChild?.getBoundingClientRect().width || 0;
  }

  /*
   * 写しを作り直す。写真の並びが変わったとき（上のギャラリーは色を替えると
   * その色の写真を先頭に回す）に呼ぶ。
   */
  refresh(index = null) {
    this.list.querySelectorAll(':scope > [data-muori-clone]').forEach((clone) => clone.remove());
    const items = this.items;
    if (items.length < 2) return;

    this.list.prepend(this.cloneOf(items[items.length - 1]));
    this.list.append(this.cloneOf(items[0]));
    this.go(index ?? 0, false);
  }

  cloneOf(item) {
    const copy = item.cloneNode(true);
    copy.setAttribute('data-muori-clone', '');
    copy.setAttribute('aria-hidden', 'true');
    copy.inert = true;
    copy.classList.remove('is-active');
    [copy, ...copy.querySelectorAll('[id], [data-media-id]')].forEach((element) => {
      element.removeAttribute('id');
      element.removeAttribute('data-media-id');
    });
    // 写しの動画は再生しない（本物と二重に読み込まれる）。3D モデルや埋め込み
    // 動画は外し、Dawn が置いている静止画だけを残す。
    copy.querySelectorAll('video').forEach((video) => {
      video.removeAttribute('autoplay');
      video.autoplay = false;
      video.preload = 'none';
    });
    copy.querySelectorAll('model-viewer, iframe').forEach((element) => element.remove());
    return copy;
  }

  /* 写しも数えた位置。0 が最後の写し、1 が本物の最初。 */
  position() {
    return Math.round(this.list.scrollLeft / this.width);
  }

  /* いま見えている本物の写真の番号（0 から）。 */
  current() {
    const count = this.items.length;
    return (((this.position() - 1) % count) + count) % count;
  }

  /* 写真の途中ではなく、どれか1枚にちょうど止まっているか。 */
  onSlide() {
    const width = this.width;
    return width > 0 && Math.abs(this.list.scrollLeft - this.position() * width) <= 1;
  }

  /* index は本物の番号。-1 と count は前後の写しを指す。 */
  go(index, smooth = true) {
    const left = (index + 1) * this.width;
    if (smooth) {
      this.animate(left);
    } else {
      this.jump(left);
    }
  }

  /*
   * 位置を直接書く。列の `scroll-behavior` は CSS で auto にしてあるので、
   * 書いた位置へすぐ移る（muori-product.css）。ここで style を書き換えると、
   * 下の再掲が見張っている変化になり、1コマごとに再掲の作り直しが走る。
   */
  setLeft(left) {
    this.list.scrollLeft = left;
  }

  /* 一瞬で移す。動いている途中なら止める。 */
  jump(left) {
    this.stop();
    this.setLeft(left);
  }

  /*
   * なめらかに動かす。ブラウザのなめらかスクロールは速さも止まり方もブラウザ
   * ごとに違うので、ここで動かす。0.4秒、最初は速く終わりはゆっくり。
   * 着いたら止まったときの処理（settle）をする。
   */
  animate(left, duration = 400) {
    this.stop();
    const from = this.list.scrollLeft;
    const start = performance.now();
    this.destination = left;
    const frame = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      this.setLeft(from + (left - from) * eased);
      if (t < 1) {
        this.animation = requestAnimationFrame(frame);
      } else {
        this.animation = null;
        this.settle();
      }
    };
    this.animation = requestAnimationFrame(frame);
  }

  stop() {
    if (this.animation) cancelAnimationFrame(this.animation);
    this.animation = null;
  }

  /* 動いている途中なら、行き先に着いたことにして止める。 */
  finish() {
    if (!this.animation) return;
    this.stop();
    this.setLeft(this.destination);
    this.settle();
  }

  /*
   * 矢印。動いている間にもう一度押されたら、途中の位置ではなく行き先から
   * 数える。行き先が写しなら、先に本物へ移ってから進む。返すのは行き先の
   * 本物の番号。
   */
  step(delta) {
    const count = this.items.length;
    if (count < 2) return 0;

    let from = this.target ?? this.position() - 1;
    if (from < 0 || from >= count) {
      from = (from + count) % count;
      this.go(from, false);
    }
    this.target = from + delta;
    this.go(this.target);
    return (((this.target % count) + count) % count);
  }

  /* 指で払う
     ------------------------------------------------------------------ */

  onTouchStart(event) {
    // 2本目の指（ピンチ）が来たら払うのをやめ、途中なら元の写真へ戻す。
    if (event.touches.length > 1) {
      if (this.drag?.axis === 'x') this.animate(Math.round(this.drag.left / this.width) * this.width, 250);
      this.drag = null;
      return;
    }
    this.drag = null;
    this.dragged = false;
    // 3D モデルは、横に動かすとモデルが回る。
    if (event.target.closest('model-viewer, .shopify-model-viewer-ui')) return;

    // 動いている途中に触ったら、行き先に着いたことにしてから払い始める。
    // 続けて素早く払ったときに、2回で1枚しか進まないことが無いように。
    this.finish();
    this.target = null;
    this.unwrap();

    const touch = event.touches[0];
    this.drag = {
      x: touch.clientX,
      y: touch.clientY,
      dx: 0,
      left: this.list.scrollLeft,
      time: performance.now(),
      axis: null,
    };
  }

  onTouchMove(event) {
    const drag = this.drag;
    if (!drag) return;
    const touch = event.touches[0];
    const dx = touch.clientX - drag.x;
    const dy = touch.clientY - drag.y;

    // 動き始めの向きで、横（写真を送る）か縦（ページのスクロール）かを決める。
    if (!drag.axis) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return;
      drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (drag.axis !== 'x') return;
    if (event.cancelable) event.preventDefault();

    // 1回に動くのは1枚まで。
    const width = this.width;
    drag.dx = Math.max(-width, Math.min(width, dx));
    this.dragged = true;
    this.setLeft(drag.left - drag.dx);
  }

  /*
   * 離したら、払った向きの次の1枚へ。少し動かしただけでも進む。ごく短い
   * 動き（タップや手ぶれ）は、元の写真に戻す。取り消されたとき（iOS の
   * 端からの「戻る」操作をやめた、など）も元の写真に戻す。
   */
  onTouchEnd(cancelled) {
    const drag = this.drag;
    this.drag = null;
    if (!drag || drag.axis !== 'x') return;
    // 払ったあとのクリックは捨てる（上の click）。次の操作には持ち越さない。
    setTimeout(() => {
      this.dragged = false;
    }, 300);

    const width = this.width;
    const base = Math.round(drag.left / width);
    const speed = Math.abs(drag.dx) / Math.max(1, performance.now() - drag.time);
    const moved = !cancelled && (Math.abs(drag.dx) >= 20 || (Math.abs(drag.dx) >= 10 && speed > 0.3));
    const step = moved ? (drag.dx < 0 ? 1 : -1) : 0;
    this.animate((base + step) * width, 400);
  }

  /* 止まったら
     ------------------------------------------------------------------ */

  /*
   * ここで動かしていないスクロール（サムネイルを押したときの Dawn の
   * scrollTo、トラックパッドの横スクロールなど）も、止まったら1枚に揃えて
   * 状態を合わせる。止まったとみなすのは、スクロールが 120ms 途切れたとき。
   */
  onScroll() {
    if (this.drag || this.animation) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      if (this.drag || this.animation) return;
      if (this.onSlide()) {
        this.settle();
      } else {
        this.animate(this.position() * this.width, 250);
      }
    }, 120);
  }

  settle() {
    clearTimeout(this.timer);
    if (!this.onSlide()) return;

    this.target = null;
    this.unwrap();
    this.onSettle?.(this.current());
  }

  /*
   * 写しの上に止まっていたら、写真の枚数ぶん先の本物へ移す。写しは本物と
   * 同じ絵なので、見た目は変わらない。止まったときと、指を置いたとき。
   */
  unwrap() {
    const count = this.items.length;
    if (count < 2 || !this.onSlide()) return;

    const position = this.position();
    if (position === 0) this.setLeft(count * this.width);
    if (position === count + 1) this.setLeft(this.width);
  }
};
